import { useState, useRef, useCallback } from 'react';
import { Play, Pause, SkipBack, SkipForward, Shuffle, Repeat, Repeat1, Volume2, VolumeX, ChevronUp, ChevronDown, ShoppingBag, X } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import { useNavigate } from 'react-router-dom';
import { Slider } from '../components/ui/slider';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const Player = () => {
  const {
    currentTrack,
    isPlaying,
    progress,
    duration,
    volume,
    shuffle,
    repeat,
    togglePlay,
    seek,
    setVolume,
    playNext,
    playPrevious,
    toggleShuffle,
    toggleRepeat,
    setCurrentTrack,
  } = usePlayer();
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const progressRef = useRef(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const swipeThreshold = 50;

  // Handle touch/swipe gestures
  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;
    const deltaX = touchEndX - touchStartX.current;
    const deltaY = touchEndY - touchStartY.current;

    // Horizontal swipe for skip (only if not dragging progress)
    if (!isDragging && Math.abs(deltaX) > swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY)) {
      if (deltaX > 0) {
        playPrevious();
      } else {
        playNext();
      }
    }
    
    // Vertical swipe for expand/collapse
    if (Math.abs(deltaY) > swipeThreshold && Math.abs(deltaY) > Math.abs(deltaX)) {
      if (deltaY < 0) {
        setExpanded(true);
      } else {
        setExpanded(false);
      }
    }
  }, [isDragging, playNext, playPrevious]);

  // Progress bar touch handling
  const handleProgressTouch = useCallback((e) => {
    if (!progressRef.current || !duration) return;
    const rect = progressRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    const percent = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
    seek(percent * duration);
  }, [duration, seek]);

  const handleProgressTouchStart = useCallback((e) => {
    setIsDragging(true);
    handleProgressTouch(e);
  }, [handleProgressTouch]);

  const handleProgressTouchMove = useCallback((e) => {
    if (isDragging) {
      handleProgressTouch(e);
    }
  }, [isDragging, handleProgressTouch]);

  const handleProgressTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  if (!currentTrack) return null;

  const progressPercent = duration > 0 ? (progress / duration) * 100 : 0;

  // Sticky Buy Bar: when a 30s preview ends on a paid track, replace
  // the normal controls with a big Buy CTA and keep the bar visible.
  if (currentTrack.previewEnded) {
    const price = typeof currentTrack.price === 'number' ? currentTrack.price : null;
    const buyLabel = price !== null ? `Buy $${price.toFixed(2)}` : 'Buy to unlock';
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-orange-500/30 bg-gradient-to-r from-orange-500/10 via-pink-500/5 to-transparent"
        data-testid="music-player-buy-bar"
      >
        <div className="container py-3">
          <div className="flex items-center gap-3 md:gap-4">
            <img
              src={currentTrack.cover_url}
              alt={currentTrack.title}
              className="rounded-lg object-cover flex-shrink-0 w-12 h-12"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium truncate text-sm">{currentTrack.title}</h4>
                <span className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/30">
                  Preview ended
                </span>
              </div>
              <p className="text-xs text-gray-400 truncate">Buy to hear the full song</p>
            </div>
            <button
              onClick={() => {
                setCurrentTrack(null);
                navigate(`/music/${currentTrack.id}`);
              }}
              className="relative flex items-center gap-2 px-4 md:px-5 py-2 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-semibold whitespace-nowrap hover:scale-105 transition-transform shadow-lg shadow-orange-500/40"
              data-testid="buy-bar-buy-button"
            >
              <span className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-500 to-pink-500 animate-ping opacity-40" />
              <ShoppingBag className="w-4 h-4 relative z-10" />
              <span className="relative z-10">{buyLabel}</span>
            </button>
            <button
              onClick={() => setCurrentTrack(null)}
              className="text-gray-500 hover:text-white p-1 transition"
              aria-label="Dismiss"
              data-testid="buy-bar-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 glass border-t border-white/10 transition-all duration-300 ${expanded ? 'h-auto' : ''}`}
      data-testid="music-player"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Progress bar (touch-friendly) */}
      <div 
        ref={progressRef}
        className="absolute top-0 left-0 right-0 h-2 md:h-1 bg-white/10 cursor-pointer group touch-none"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const percent = (e.clientX - rect.left) / rect.width;
          seek(percent * duration);
        }}
        onTouchStart={handleProgressTouchStart}
        onTouchMove={handleProgressTouchMove}
        onTouchEnd={handleProgressTouchEnd}
      >
        <div 
          className="h-full bg-gradient-to-r from-purple-500 to-cyan-500"
          style={{ width: `${progressPercent}%` }}
        />
        <div 
          className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 md:w-3 md:h-3 bg-white rounded-full shadow-lg transition-opacity ${isDragging ? 'opacity-100 scale-125' : 'opacity-0 group-hover:opacity-100'}`}
          style={{ left: `${progressPercent}%`, transform: 'translate(-50%, -50%)' }}
        />
      </div>

      <div className="container py-3 pt-4 md:pt-3">
        {/* Mobile expand/collapse indicator */}
        <button 
          className="md:hidden absolute top-1 left-1/2 -translate-x-1/2 text-gray-400 p-1"
          onClick={() => setExpanded(!expanded)}
          data-testid="expand-button"
        >
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
        </button>

        <div className={`flex items-center justify-between gap-4 ${expanded ? 'flex-col md:flex-row' : ''}`}>
          {/* Track Info */}
          <div className={`flex items-center gap-4 min-w-0 ${expanded ? 'w-full justify-center md:justify-start md:flex-1' : 'flex-1'}`}>
            <img 
              src={currentTrack.cover_url} 
              alt={currentTrack.title}
              className={`rounded-lg object-cover flex-shrink-0 transition-all ${expanded ? 'w-24 h-24 md:w-12 md:h-12' : 'w-12 h-12'}`}
              data-testid="player-cover"
            />
            <div className={`min-w-0 ${expanded ? 'text-center md:text-left' : ''}`}>
              <h4 className={`font-medium truncate ${expanded ? 'text-base md:text-sm' : 'text-sm'}`} data-testid="player-title">
                {currentTrack.title}
              </h4>
              <p className={`text-gray-400 truncate ${expanded ? 'text-sm md:text-xs' : 'text-xs'}`} data-testid="player-artist">
                {currentTrack.artist}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className={`flex items-center gap-3 md:gap-4 ${expanded ? 'w-full justify-center py-4 md:py-0' : ''}`}>
            <button
              onClick={toggleShuffle}
              className={`player-btn ${expanded ? 'flex' : 'hidden sm:flex'} ${shuffle ? 'text-cyan-400' : ''}`}
              data-testid="shuffle-button"
            >
              <Shuffle className="w-5 h-5 md:w-4 md:h-4" />
            </button>
            
            <button 
              onClick={playPrevious}
              className="player-btn p-2"
              data-testid="prev-button"
            >
              <SkipBack className="w-6 h-6 md:w-5 md:h-5" />
            </button>
            
            <button 
              onClick={togglePlay}
              className={`player-btn-main ${expanded ? 'w-14 h-14 md:w-10 md:h-10' : ''}`}
              data-testid="play-pause-button"
            >
              {isPlaying ? <Pause className="w-6 h-6 md:w-5 md:h-5" /> : <Play className="w-6 h-6 md:w-5 md:h-5 ml-0.5" />}
            </button>
            
            <button 
              onClick={playNext}
              className="player-btn p-2"
              data-testid="next-button"
            >
              <SkipForward className="w-6 h-6 md:w-5 md:h-5" />
            </button>
            
            <button
              onClick={toggleRepeat}
              className={`player-btn ${expanded ? 'flex' : 'hidden sm:flex'} ${repeat !== 'off' ? 'text-purple-400' : ''}`}
              data-testid="repeat-button"
            >
              {repeat === 'one' ? <Repeat1 className="w-5 h-5 md:w-4 md:h-4" /> : <Repeat className="w-5 h-5 md:w-4 md:h-4" />}
            </button>
          </div>

          {/* Time & Volume - show on expanded mobile or desktop */}
          <div className={`items-center gap-4 flex-1 justify-end ${expanded ? 'flex w-full md:w-auto justify-center md:justify-end py-2 md:py-0' : 'hidden md:flex'}`}>
            <span className="text-xs text-gray-400 font-mono min-w-[80px] text-center md:text-right">
              {formatTime(progress)} / {formatTime(duration)}
            </span>
            
            <div className={`flex items-center gap-2 ${expanded ? 'w-40 md:w-32' : 'w-32'}`}>
              <button
                onClick={() => setVolume(volume === 0 ? 0.7 : 0)}
                className="player-btn"
                data-testid="volume-button"
              >
                {volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
              </button>
              <Slider
                value={[volume * 100]}
                max={100}
                step={1}
                onValueChange={([val]) => setVolume(val / 100)}
                className="flex-1"
                data-testid="volume-slider"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Swipe hint for mobile - only show briefly on first load */}
      <div className="md:hidden absolute -top-8 left-1/2 -translate-x-1/2 text-xs text-gray-500 pointer-events-none opacity-0">
        Swipe left/right to skip
      </div>
    </div>
  );
};
